import { Component } from '@theme/component';

/**
 * Adds a critically damped spring to document wheel scrolling. The real viewport
 * moves (no transformed page wrapper), preserving sticky heroes, anchors and focus.
 * Touch, keyboard navigation and independent scroll containers remain native.
 *
 * @extends Component
 */
class VelocityScroll extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #frame = 0;
  #lastTime = 0;
  #position = 0;
  #target = 0;
  #velocity = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#stop();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('wheel', this.#onWheel, { passive: false, signal });
    document.addEventListener('scroll', this.#onNativeScroll, { passive: true, signal });
    document.addEventListener('pointerdown', this.#stop, { capture: true, passive: true, signal });
    document.addEventListener('touchstart', this.#stop, { passive: true, signal });
    document.addEventListener('keydown', this.#stop, { capture: true, signal });
    document.addEventListener('visibilitychange', this.#stop, { signal });
    window.addEventListener('resize', this.#stop, { passive: true, signal });
    window.addEventListener('blur', this.#stop, { signal });
    window.addEventListener('pagehide', this.#stop, { signal });
    window.addEventListener('pageshow', this.#stop, { signal });
    window.addEventListener('hashchange', this.#stop, { signal });
    this.#reducedMotion.addEventListener('change', this.#stop, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#stop();
    super.disconnectedCallback();
  }

  #stop = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#lastTime = 0;
    this.#velocity = 0;
    this.#position = window.scrollY;
    this.#target = this.#position;
  };

  get #locked() {
    return this.#reducedMotion.matches || document.hidden ||
      document.documentElement.hasAttribute('scroll-lock') ||
      Boolean(document.querySelector('dialog[open], [scroll-lock][open]'));
  }

  get #maximum() {
    const scroller = document.scrollingElement;
    return scroller ? Math.max(0, scroller.scrollHeight - scroller.clientHeight) : 0;
  }

  /** @param {WheelEvent} event - A wheel event bubbling from storefront content. */
  #usesNativeScrolling(event) {
    for (const node of event.composedPath()) {
      if (node === document.body || node === document.documentElement) break;
      if (!(node instanceof Element)) continue;
      if (node.matches('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-native-scroll]')) {
        return true;
      }
      const style = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) {
        return true;
      }
    }
    return false;
  }

  /** @param {WheelEvent} event - Only cancel vertical page-wheel gestures. */
  #onWheel = (event) => {
    if (event.defaultPrevented || !event.cancelable || this.#locked || event.ctrlKey ||
      event.metaKey || event.altKey || event.shiftKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY) ||
      this.#usesNativeScrolling(event)) {
      this.#stop();
      return;
    }

    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    const delta = event.deltaY * unit;
    const maximum = this.#maximum;
    if (!Number.isFinite(delta) || delta === 0 || maximum === 0) return;

    if (!this.#frame) {
      this.#position = window.scrollY;
      this.#target = this.#position;
      this.#velocity = 0;
    }

    // A reversal responds immediately instead of consuming an old scroll queue.
    if (delta * (this.#target - this.#position) < 0) {
      this.#target = this.#position;
      this.#velocity = 0;
    }

    // Bound the queued distance so rapid gestures never create seconds of lag.
    const lead = window.innerHeight * 1.5;
    this.#target = Math.max(0, Math.min(maximum,
      Math.max(this.#position - lead, Math.min(this.#position + lead, this.#target + delta))));
    event.preventDefault();

    if (!this.#frame) {
      this.#lastTime = performance.now();
      this.#frame = requestAnimationFrame(this.#tick);
    }
  };

  // Cancel inertia if an anchor, focus change, scrollbar or another theme feature
  // moves the viewport; a pending frame must not undo that navigation.
  #onNativeScroll = () => {
    if (this.#frame && Math.abs(window.scrollY - this.#position) > 1) this.#stop();
  };

  /** @param {number} time - Animation-frame timestamp. */
  #tick = (time) => {
    if (this.#locked) {
      this.#stop();
      return;
    }

    const dt = Math.min(Math.max((time - this.#lastTime) / 1000, 0), 0.05);
    this.#lastTime = time;
    const maximum = this.#maximum;
    this.#target = Math.max(0, Math.min(maximum, this.#target));

    // Exact critical-damping integration is stable at 60/120Hz and cannot bounce
    // beyond a stationary target. A frequency of 13 gives a weighty ~400ms ease.
    const frequency = 13;
    const error = this.#position - this.#target;
    const decay = Math.exp(-frequency * dt);
    const impulse = (this.#velocity + frequency * error) * dt;
    this.#position = Math.max(0, Math.min(maximum, this.#target + (error + impulse) * decay));
    this.#velocity = (this.#velocity - frequency * impulse) * decay;

    const settled = Math.abs(this.#target - this.#position) < 0.5 && Math.abs(this.#velocity) < 5;
    if (settled) this.#position = this.#target;
    window.scrollTo({ top: this.#position, left: window.scrollX, behavior: 'instant' });

    if (settled) this.#stop();
    else this.#frame = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('velocity-scroll')) customElements.define('velocity-scroll', VelocityScroll);
